import React from 'react';
import { AlertTriangle } from 'lucide-react';
import { C } from '../theme.js';
import { Button, Code } from './Primitives.jsx';

// ── Top-level error boundary ─────────────────────────────────────────────
// React only lets class components catch render errors — no hook
// equivalent exists. Catches errors thrown while rendering/committing any
// descendant (an uncaught exception used to unmount the whole tree, leaving
// a blank page with only the message in the console — see
// .claude/docs/changelog.md's 2026-09-15 entries for several examples).
// Does NOT catch errors in event handlers or async callbacks (those already
// have their own try/catch throughout the app, e.g. api.js call sites).
export class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // Surfaced in the console with the component stack so it's still
    // diagnosable from a bug report, same as the uncaught errors were before.
    console.error('Uncaught render error:', error, info?.componentStack);
  }

  reset = () => {
    this.setState({ error: null });
  };

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;
    return (
      <div style={{
        minHeight: '100vh', background: C.app, color: C.fg1,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 24,
      }}>
        <div style={{
          maxWidth: 560, width: '100%', background: C.surface,
          border: `1px solid ${C.border}`, borderRadius: 12, padding: 28,
        }}>
          <div style={{ color: C.warn, marginBottom: 6 }}><AlertTriangle size={26} /></div>
          <div style={{ fontSize: '1.05rem', fontWeight: 700, color: C.fg1, marginBottom: 8 }}>
            Something went wrong
          </div>
          <div style={{ fontSize: '.85rem', color: C.fg3, lineHeight: 1.5, marginBottom: 16 }}>
            This screen hit an unexpected error and couldn&apos;t finish rendering.
            Your work up to the last save is safe. Try reloading — if it keeps
            happening, share the error below.
          </div>
          <Code style={{ display: 'block', marginBottom: 20, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {error.message || String(error)}
          </Code>
          <div style={{ display: 'flex', gap: 10 }}>
            <Button kind="primary" onClick={() => window.location.reload()}>Reload</Button>
            <Button kind="ghost" onClick={this.reset}>Try to continue</Button>
          </div>
        </div>
      </div>
    );
  }
}
